import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bell, BellSlash, BellRinging, X, CalendarCheck, Clock, Lightning, Barbell, UserMinus, Trophy,
  CheckCircle, Funnel, DotsThree,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { PUSH_SUPPORTED, getPermissionState, isSubscribed, subscribe, unsubscribe } from "@/lib/push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  phase_ending:      { label: "Fase in scadenza",   Icon: Lightning,      color: "#FF9500" },
  appt_1h:           { label: "Appuntamento 1h",    Icon: Clock,          color: "#FF3B30" },
  appt_24h:          { label: "Appuntamento 24h",   Icon: CalendarCheck,  color: "#007AFF" },
  client_no_workout: { label: "Piano mancante",     Icon: Barbell,        color: "#D4FF00" },
  client_inactive:   { label: "Cliente inattivo",   Icon: UserMinus,      color: "#FF3B30" },
  milestone:         { label: "Milestone",          Icon: Trophy,         color: "#34C759" },
};

const PRIORITY_STYLE = {
  high:   { bar: "bg-[#FF3B30]", label: "text-[#FF3B30]" },
  medium: { bar: "bg-[#FF9500]", label: "text-[#FF9500]" },
  low:    { bar: "bg-zinc-600",  label: "text-zinc-400" },
};

const FILTERS = [
  { id: "all", label: "Tutte" },
  { id: "unread", label: "Non lette" },
  { id: "high", label: "Priorità alta" },
];

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return "ora";
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    const d = Math.floor(s / 86400);
    return d < 7 ? `${d}g` : new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

export default function NotificationCenter({ open, onClose, onUnreadChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [pushStatus, setPushStatus] = useState("loading"); // "unsupported" | "off" | "on" | "denied" | "loading"
  const navigate = useNavigate();

  const [reminderMin, setReminderMin] = useState(30);
  const [reminderEnabled, setReminderEnabled] = useState(true);

  // Initialize push status on mount
  useEffect(() => {
    const initPushStatus = async () => {
      if (!PUSH_SUPPORTED) {
        setPushStatus("unsupported");
        return;
      }
      const perm = getPermissionState();
      if (perm === "denied") {
        setPushStatus("denied");
        return;
      }
      const subscribed = await isSubscribed();
      setPushStatus(subscribed ? "on" : "off");
    };
    initPushStatus();
  }, []);

  // Load appointment-reminder settings
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/appointment-reminders`);
        if (cancelled) return;
        setReminderEnabled(data.enabled !== false);
        setReminderMin(Number(data.minutes_before ?? 30));
      } catch (err) { console.debug("[NotificationCenter]", err); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const updateReminderMin = useCallback(async (next) => {
    const value = Number(next);
    setReminderMin(value);
    setReminderEnabled(value > 0);
    try {
      await axios.put(`${API}/settings/appointment-reminders`, {
        enabled: value > 0,
        minutes_before: value > 0 ? value : 30,
      });
    } catch (err) { console.debug("[NotificationCenter]", err); }
  }, []);

  const togglePush = useCallback(async () => {
    if (pushStatus === "loading" || pushStatus === "denied" || pushStatus === "unsupported") return;
    setPushStatus("loading");
    try {
      if (pushStatus === "on") {
        await unsubscribe();
        setPushStatus("off");
      } else {
        await subscribe();
        setPushStatus("on");
      }
    } catch (err) {
      console.error("Push toggle error:", err);
      const perm = getPermissionState();
      if (perm === "denied") {
        setPushStatus("denied");
      } else {
        setPushStatus("off");
      }
    }
  }, [pushStatus]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setItems(data.items || []);
      onUnreadChange?.(data.unread_count || 0);
    } catch (err) { console.debug("[NotificationCenter]", err); } finally {
      setLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);

  const markRead = useCallback(async (keys) => {
    if (!keys.length) return;
    setItems((prev) => prev.map((n) => keys.includes(n.key) ? { ...n, read: true } : n));
    try { await axios.post(`${API}/notifications/mark-read`, { keys }); } catch (err) { console.debug("[NotificationCenter]", err); }
    fetchItems();
  }, [fetchItems]);

  const markAllRead = useCallback(async () => {
    try { await axios.post(`${API}/notifications/mark-all-read`); } catch (err) { console.debug("[NotificationCenter]", err); }
    fetchItems();
  }, [fetchItems]);

  const handleAction = useCallback((n) => {
    const a = n.action || {};
    if (!n.read) markRead([n.key]);
    if (a.type === "open_client") {
      navigate(`/clients/${a.client_id}`);
    } else if (a.type === "open_calendar") {
      navigate("/calendar");
    }
    onClose?.();
  }, [navigate, onClose, markRead]);

  const filtered = useMemo(() => {
    if (filter === "unread") return items.filter((n) => !n.read);
    if (filter === "high") return items.filter((n) => n.priority === "high");
    return items;
  }, [items, filter]);

  const counts = useMemo(() => ({
    all: items.length,
    unread: items.filter((n) => !n.read).length,
    high: items.filter((n) => n.priority === "high").length,
  }), [items]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={onClose}
            data-testid="notifications-backdrop"
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-[#0A0A0A] border-l border-[#1A1A1A] z-50 flex flex-col"
            data-testid="notifications-panel"
          >
            <div className="px-5 py-4 border-b border-[#1A1A1A] flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Bell size={18} weight="fill" className="text-[#007AFF]" />
                <h2 className="text-base font-bold uppercase tracking-tight text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  NOTIFICHE
                </h2>
                {counts.unread > 0 && (
                  <span className="bg-[#FF3B30] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-testid="notif-header-count">
                    {counts.unread}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {pushStatus !== "unsupported" && (
                  <button
                    onClick={togglePush}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                      pushStatus === "on"
                        ? "bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30"
                        : pushStatus === "denied"
                          ? "bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 cursor-not-allowed"
                          : "bg-[#171717] text-zinc-400 border border-[#262626] hover:text-white"
                    }`}
                    disabled={pushStatus === "denied" || pushStatus === "loading"}
                    data-testid="push-toggle"
                    title={
                      pushStatus === "on" ? "Push attive (clicca per disattivare)"
                        : pushStatus === "denied" ? "Permesso negato dal browser"
                          : "Attiva notifiche push"
                    }
                  >
                    {pushStatus === "on" ? <BellRinging size={10} weight="fill" /> : <Bell size={10} weight="regular" />}
                    <span>
                      {pushStatus === "on" ? "Push ON" : pushStatus === "denied" ? "Bloccate" : "Push OFF"}
                    </span>
                  </button>
                )}
                {counts.unread > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[11px] text-[#007AFF] hover:text-[#3395FF] font-medium"
                    data-testid="notif-mark-all-read"
                  >
                    Segna tutte
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center"
                  aria-label="Chiudi"
                  data-testid="notif-close"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>

            <div className="px-5 py-3 border-b border-[#1A1A1A] flex items-center gap-2 overflow-x-auto">
              <Funnel size={12} className="text-zinc-600 flex-shrink-0" />
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${
                    filter === f.id
                      ? "bg-[#007AFF] text-white"
                      : "bg-[#171717] border border-[#262626] text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`notif-filter-${f.id}`}
                >
                  {f.label} {counts[f.id] > 0 && <span className="ml-1 opacity-60">{counts[f.id]}</span>}
                </button>
              ))}
            </div>

            <div className="px-5 py-2.5 border-b border-[#1A1A1A] flex items-center justify-between gap-3" data-testid="appt-reminder-row">
              <div className="flex items-center gap-2 min-w-0">
                <CalendarCheck size={13} className="text-zinc-500 flex-shrink-0" />
                <span className="text-[11px] text-zinc-400 truncate">
                  Promemoria appuntamento
                </span>
              </div>
              <select
                value={reminderEnabled ? reminderMin : 0}
                onChange={(e) => updateReminderMin(e.target.value)}
                className="bg-[#171717] border border-[#262626] text-white text-[11px] rounded-md px-2 py-1 cursor-pointer hover:border-[#333] focus:outline-none focus:border-[#007AFF]"
                data-testid="appt-reminder-select"
              >
                <option value={0}>Off</option>
                <option value={15}>15 min prima</option>
                <option value={30}>30 min prima</option>
                <option value={60}>1 ora prima</option>
                <option value={120}>2 ore prima</option>
              </select>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="notif-list">
              {loading && items.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                  <BellSlash size={36} weight="duotone" className="text-zinc-600 mb-3" />
                  <p className="text-sm font-medium text-zinc-400">Nessuna notifica</p>
                  <p className="text-xs text-zinc-600 mt-1">
                    {filter === "unread" ? "Hai letto tutte le notifiche" : filter === "high" ? "Nessuna notifica ad alta priorità" : "Torneremo qui quando ci sarà qualcosa di rilevante"}
                  </p>
                </div>
              ) : (
                <ul>
                  {filtered.map((n, i) => (
                    <NotificationRow key={n.key} n={n} index={i} onAction={handleAction} onMarkRead={markRead} />
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function NotificationRow({ n, index, onAction, onMarkRead }) {
  const meta = TYPE_META[n.type] || { label: n.type, Icon: Bell, color: "#007AFF" };
  const pr = PRIORITY_STYLE[n.priority] || PRIORITY_STYLE.low;
  const Icon = meta.Icon;
  return (
    <motion.li
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: Math.min(index, 10) * 0.02 }}
      className={`border-b border-[#1A1A1A] relative ${n.read ? "" : "bg-[#0F0F0F]"}`}
      data-testid={`notif-item-${n.type}`}
      data-read={n.read ? "true" : "false"}
      data-priority={n.priority}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${n.read ? "bg-transparent" : pr.bar}`} />
      <div className="flex items-start gap-3 px-5 py-3.5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${meta.color}22`, border: `1px solid ${meta.color}44` }}
        >
          <Icon size={16} weight="fill" style={{ color: meta.color }} />
        </div>
        <button
          type="button"
          onClick={() => onAction(n)}
          className="flex-1 min-w-0 text-left outline-none"
          data-testid={`notif-action-${n.key}`}
        >
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-[9px] font-bold uppercase tracking-wider ${pr.label}`}>{n.priority}</span>
            <span className="text-[9px] text-zinc-600">•</span>
            <span className="text-[10px] text-zinc-500">{meta.label}</span>
            <span className="text-[9px] text-zinc-600">•</span>
            <span className="text-[10px] text-zinc-500">{timeAgo(n.created_at)}</span>
            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF] ml-auto" />}
          </div>
          <p className={`text-sm font-semibold leading-tight ${n.read ? "text-zinc-400" : "text-white"}`}>{n.title}</p>
          <p className={`text-xs leading-relaxed mt-0.5 ${n.read ? "text-zinc-600" : "text-zinc-400"} line-clamp-2`}>
            {n.message}
          </p>
        </button>
        {!n.read && (
          <button
            type="button"
            onClick={() => onMarkRead([n.key])}
            className="w-7 h-7 rounded-full text-zinc-600 hover:text-[#34C759] hover:bg-[#34C759]/10 flex items-center justify-center flex-shrink-0"
            aria-label="Segna come letta"
            data-testid={`notif-mark-read-${n.key}`}
            title="Segna come letta"
          >
            <CheckCircle size={14} weight="fill" />
          </button>
        )}
      </div>
    </motion.li>
  );
}

export { DotsThree };
